/**
 * @page DisplayManagerPage
 * @description 電視顯示螢幕管理（最高管理員 / 場管）
 * Route: /display
 * UI Reference: UI-5-1
 */

import AssignTournamentModal from './AssignTournamentModal';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.ts';
import { useCurrentVenue } from '../../hooks/useCurrentVenue';
import { useAuthStore } from '../../store/auth.store';
import { displayScreenPath } from '../../lib/displayScreenLink';

interface DisplayScreen {
  id: string;
  name: string;
  token: string;
  slug: string | null;
  tournamentId: string | null;
  layout: string;
  isActive: boolean;
  insetPercent: number;
}

interface Tournament {
  id: string;
  name: string;
  status: string;
}

interface PairingStatus {
  id?: string;
  pending: boolean;
  expiresAt: string | null;
}

export function PairingControls({
  screenId,
  screenName,
  venueName,
}: {
  screenId: string;
  screenName: string;
  venueName: string;
}) {
  const { t } = useTranslation('display');
  const qc = useQueryClient();
  const [revealedCode, setRevealedCode] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [recoveryApproved, setRecoveryApproved] = useState(false);
  useEffect(() => {
    setIdentifier('');
    setConfirmed(false);
    setRecoveryApproved(false);
  }, [screenId, screenName, venueName]);
  const { data: status } = useQuery<PairingStatus>({
    queryKey: ['display-pairing-status', screenId],
    queryFn: () => api.get<PairingStatus>(`/display/screens/${screenId}/pairing-status`),
  });
  const create = useMutation({
    mutationFn: () => api.post<{ code: string }>(`/display/screens/${screenId}/pairing-codes`),
    onSuccess: (result) => {
      setRevealedCode(result.code);
      void qc.invalidateQueries({ queryKey: ['display-pairing-status', screenId] });
    },
  });
  const recovery = useMutation({
    mutationFn: () => api.post(`/display/screens/${screenId}/pairing-recovery`, { identifier }),
    onSuccess: () => {
      setRevealedCode('');
      setRecoveryApproved(true);
      void qc.invalidateQueries({ queryKey: ['display-pairing-status', screenId] });
    },
  });
  const revoke = useMutation({
    mutationFn: (pairingId: string) =>
      api.delete(`/display/screens/${screenId}/pairing-codes/${pairingId}`),
    onSuccess: () => {
      setRevealedCode('');
      void qc.invalidateQueries({ queryKey: ['display-pairing-status', screenId] });
    },
  });
  const revokeSessions = useMutation({
    mutationFn: () => api.post(`/display/screens/${screenId}/revoke-sessions`),
    onSuccess: () => {
      setRevealedCode('');
      void qc.invalidateQueries({ queryKey: ['display-pairing-status', screenId] });
    },
  });

  return (
    <div className="space-y-2 border-t border-base-300/30 pt-3">
      {revealedCode && (
        <div role="status" className="rounded-lg bg-primary/10 p-3 text-center">
          <p className="text-xs text-base-content/60">
            {t('pairingCodeOnce', { defaultValue: '配對碼僅顯示一次' })}
          </p>
          <p className="font-mono text-3xl font-black tracking-[0.2em]">{revealedCode}</p>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => create.mutate()}
          disabled={create.isPending}
          className="rounded-lg border border-primary/40 px-3 py-1.5 text-xs font-bold text-primary disabled:opacity-50"
        >
          {status?.pending
            ? t('replacePairingCode', { defaultValue: '更換配對碼' })
            : t('createPairingCode', { defaultValue: '建立配對碼' })}
        </button>
        {status?.pending && status.id && (
          <button
            type="button"
            onClick={() => revoke.mutate(status.id!)}
            disabled={revoke.isPending}
            className="rounded-lg border border-warning/40 px-3 py-1.5 text-xs text-warning disabled:opacity-50"
          >
            {t('revokePairingCode', { defaultValue: '撤銷待配對碼' })}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            if (
              confirm(
                t('confirmRevokeSessions', { defaultValue: '確定撤銷這台電視的所有既有連線？' }),
              )
            )
              revokeSessions.mutate();
          }}
          disabled={revokeSessions.isPending}
          className="rounded-lg border border-error/40 px-3 py-1.5 text-xs text-error disabled:opacity-50"
        >
          {t('revokeSessions', { defaultValue: '撤銷既有連線' })}
        </button>
      </div>
      <div className="space-y-2 rounded-lg border border-base-300 p-3">
        <p className="text-sm font-bold">
          {t('recoveryTitle')}: {venueName} / {screenName}
        </p>
        <label className="block text-xs">
          {t('recoveryIdentifier')}
          <input
            value={identifier}
            maxLength={39}
            autoComplete="off"
            onChange={(event) => {
              setIdentifier(event.target.value);
              setConfirmed(false);
              setRecoveryApproved(false);
            }}
            className="mt-1 w-full rounded border border-base-300 bg-base-100 p-2 font-mono"
          />
        </label>
        <label className="flex gap-2 text-xs">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          {t('recoveryConfirm', { venue: venueName, screen: screenName })}
        </label>
        <button
          type="button"
          disabled={
            !confirmed ||
            !/^[0-9a-f]{32}$/i.test(identifier.replace(/[ -]/g, '')) ||
            recovery.isPending
          }
          onClick={() => recovery.mutate()}
          className="rounded border border-primary px-3 py-2 text-xs text-primary disabled:opacity-50"
        >
          {t('recoveryApprove')}
        </button>
        {recoveryApproved && <p role="status">{t('recoveryApproved')}</p>}
      </div>
      {(create.isError || revoke.isError || revokeSessions.isError || recovery.isError) && (
        <p role="alert" className="text-xs text-error">
          {t('pairingActionFailed', { defaultValue: '配對操作失敗，請重試。' })}
        </p>
      )}
    </div>
  );
}

const LAYOUT_KEYS: Record<string, string> = {
  TIMER_FULL: 'layoutTimerFull',
  BLIND_STRUCTURE: 'layoutBlindStructure',
  PRIZE_TABLE: 'layoutPrizeTable',
  LEADERBOARD: 'layoutLeaderboard',
};

const STATUS_KEYS: Record<string, string> = {
  DRAFT: 'statusDraft',
  REGISTERED: 'statusRegistered',
  IN_PROGRESS: 'statusInProgress',
  PAUSED: 'statusPaused',
  COMPLETED: 'statusCompleted',
  CANCELLED: 'statusCancelled',
};

function QrCodeButton({
  token,
  slug,
  venueCode,
}: {
  token: string;
  slug: string | null;
  venueCode?: string;
}) {
  const { t } = useTranslation('display');
  const path = displayScreenPath(venueCode, slug, token);
  const tokenUrl = `${window.location.origin}/display/screen/${token}`;
  const url = `${window.location.origin}${path}`;
  return (
    <div className="flex flex-col gap-0.5">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-xs text-primary hover:underline"
        title={url}
      >
        <span className="material-symbols-outlined text-sm" aria-hidden="true">
          open_in_new
        </span>
        <span>{slug && venueCode ? path : t('openScreen', { token: token.slice(0, 8) })}</span>
      </a>
      {slug && (
        <span className="text-[10px] text-base-content/50" title={tokenUrl}>
          Token: {token.slice(0, 8)}…
        </span>
      )}
    </div>
  );
}

interface CreateModalProps {
  tournaments: Tournament[];
  onClose: () => void;
  onSave: (data: { name: string; layout: string; tournamentId?: string }) => void;
  loading: boolean;
}

const LAYOUT_OPTIONS = Object.keys(LAYOUT_KEYS);

function CreateModal({ tournaments, onClose, onSave, loading }: CreateModalProps) {
  const { t } = useTranslation('display');
  const [name, setName] = useState('');
  const [layout, setLayout] = useState('TIMER_FULL');
  const [tournamentId, setTournamentId] = useState('');

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-base-200 rounded-xl border border-base-300 p-8 w-full max-w-lg shadow-2xl">
        <h2 className="text-xl font-bold mb-6">{t('createScreen')}</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">{t('screenName')} *</label>
            <input
              className="w-full rounded-lg border border-base-300 bg-transparent px-4 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/40"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('screenNamePlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">{t('bindTournament')}</label>
            <select
              className="w-full rounded-lg border border-base-300 bg-base-300 px-4 py-2 text-sm text-base-content"
              value={tournamentId}
              onChange={(e) => setTournamentId(e.target.value)}
            >
              <option value="">{t('noBinding')}</option>
              {tournaments.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">{t('displayLayout')}</label>
            <select
              className="w-full rounded-lg border border-base-300 bg-base-300 px-4 py-2 text-sm text-base-content"
              value={layout}
              onChange={(e) => setLayout(e.target.value)}
            >
              {LAYOUT_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {t(LAYOUT_KEYS[k] as string)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-sm font-semibold text-base-content/50 hover:bg-base-300 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            disabled={!name.trim() || loading}
            onClick={() => onSave({ name, layout, tournamentId: tournamentId || undefined })}
            className="px-6 py-2 rounded-lg bg-primary text-black text-sm font-black hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
          >
            {loading ? t('creating') : t('create')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DisplayManagerPage() {
  const { t } = useTranslation('display');
  const qc = useQueryClient();
  const { venue, isError: venueError, refetch: refetchVenue } = useCurrentVenue();
  const revision = useAuthStore((s) => s.authRevision);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [assignTournamentId, setAssignTournamentId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<DisplayScreen | null>(null);
  const [editLayout, setEditLayout] = useState('');
  const [editTournamentId, setEditTournamentId] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editInsetPercent, setEditInsetPercent] = useState(0);

  const { data: screens = [], isLoading } = useQuery<DisplayScreen[]>({
    queryKey: ['display-screens', revision],
    enabled: Boolean(venue),
    queryFn: () => api.get<DisplayScreen[]>('/display/screens'),
  });

  const { data: tournaments = [] } = useQuery<Tournament[]>({
    queryKey: ['tournaments-active', revision],
    enabled: Boolean(venue),
    queryFn: () =>
      api
        .get<{ data: Tournament[]; total: number }>('/tournament?limit=50')
        .then((d) => d.data ?? []),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const { data: cataloguePolicy } = useQuery<{ enabled: boolean }>({
    queryKey: ['display-catalogue-policy', revision],
    enabled: Boolean(venue),
    queryFn: () => api.get<{ enabled: boolean }>('/display/catalogue-policy'),
  });
  const catalogueMutation = useMutation({
    mutationFn: (enabled: boolean) => api.patch('/display/catalogue-policy', { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['display-catalogue-policy'] }),
  });

  // Handle ?tournamentId query param from tournament detail page
  useEffect(() => {
    const tid = searchParams.get('tournamentId');
    if (tid) {
      setAssignTournamentId(tid);
      setShowAssign(true);
      // Clean up URL
      searchParams.delete('tournamentId');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const createMutation = useMutation({
    mutationFn: (body: object) => api.post<DisplayScreen>('/display/screens', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['display-screens'] });
      setShowCreate(false);
    },
    onError: (err: Error) => {
      console.warn('createScreen failed:', err.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      slug?: string | null;
      layout?: string;
      tournamentId?: string | null;
      insetPercent?: number;
    }) => api.patch<DisplayScreen>(`/display/screens/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['display-screens'] });
      setEditTarget(null);
    },
    onError: (err: Error) => {
      console.warn('updateScreen failed:', err.message);
    },
  });

  const regenTokenMutation = useMutation({
    mutationFn: (id: string) => api.post<DisplayScreen>(`/display/screens/${id}/regenerate-token`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['display-screens'] }),
    onError: (err: Error) => {
      console.warn('regenToken failed:', err.message);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/display/screens/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['display-screens'] }),
    onError: (err: Error) => {
      console.warn('removeScreen failed:', err.message);
    },
  });

  const handleEditOpen = (screen: DisplayScreen) => {
    updateMutation.reset();
    setEditTarget(screen);
    setEditLayout(screen.layout);
    setEditTournamentId(screen.tournamentId ?? '');
    setEditSlug(screen.slug ?? '');
    setEditInsetPercent(screen.insetPercent ?? 0);
  };

  if (!venue) {
    return (
      <div className="p-6" aria-live="polite">
        {venueError ? (
          <button
            type="button"
            onClick={() => void refetchVenue()}
            className="text-error underline"
          >
            {t('venueUnavailable', { defaultValue: '無法確認場館，點此重試後管理螢幕。' })}
          </button>
        ) : (
          <p>{t('venueLoading', { defaultValue: '確認場館中…' })}</p>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tight">{t('title')}</h1>
          <p className="text-base-content/60 text-base mt-1">{t('subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-black font-black text-sm hover:scale-[1.02] active:scale-95 transition-all shadow-lg shadow-primary/20"
        >
          <span className="material-symbols-outlined">add</span>
          {t('addScreen')}
        </button>
      </div>

      <label className="mb-6 flex items-center justify-between rounded-xl border border-base-300 bg-base-200 p-4">
        <span>
          <span className="block font-bold">
            {t('publicCatalogue', { defaultValue: '公開場館標籤' })}
          </span>
          <span className="text-xs text-base-content/60">
            {t('publicCatalogueHint', {
              defaultValue: '只公開場館與螢幕名稱；配對仍需要一次性配對碼。',
            })}
          </span>
        </span>
        <input
          type="checkbox"
          className="toggle toggle-primary"
          checked={cataloguePolicy?.enabled ?? false}
          disabled={!cataloguePolicy || catalogueMutation.isPending}
          onChange={(event) => catalogueMutation.mutate(event.target.checked)}
        />
      </label>

      {/* Screen Cards Grid */}
      {isLoading ? (
        <div className="text-center py-20 text-base-content/40">{t('loading')}...</div>
      ) : screens.length === 0 ? (
        <div className="text-center py-20 text-base-content/40">
          <span className="material-symbols-outlined text-6xl block mb-3">tv_off</span>
          {t('noScreens')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {screens.map((screen) => {
            const linkedTournament = tournaments.find((t) => t.id === screen.tournamentId);
            return (
              <div
                key={screen.id}
                className="bg-base-200 border border-base-300 rounded-xl overflow-hidden shadow-xl"
              >
                {/* Preview */}
                <div className="relative aspect-video bg-base-100 flex flex-col items-center justify-center group">
                  <div className="text-primary/20 absolute top-4 right-4">
                    <span className="material-symbols-outlined text-4xl">tv</span>
                  </div>
                  {screen.layout === 'TIMER_FULL' && (
                    <div className="flex flex-col items-center text-center p-6">
                      <p className="text-primary text-4xl font-black mb-2 font-mono">--:--</p>
                      <p className="text-base-content/40 text-xs uppercase tracking-widest">
                        {linkedTournament?.name ?? t('noTournamentAssigned')}
                      </p>
                      <p className="text-base-content/60 text-xs mt-1">{t('layoutTimerFull')}</p>
                    </div>
                  )}
                  {screen.layout === 'BLIND_STRUCTURE' && (
                    <div className="flex flex-col items-center text-center p-6">
                      <span className="material-symbols-outlined text-primary text-5xl mb-2">
                        table_chart
                      </span>
                      <p className="text-base-content/70 text-sm font-bold">
                        {t('layoutBlindStructure')}
                      </p>
                      <p className="text-base-content/50 text-xs mt-1">
                        {linkedTournament?.name ?? t('noTournamentAssigned')}
                      </p>
                    </div>
                  )}
                  {screen.layout === 'PRIZE_TABLE' && (
                    <div className="flex flex-col items-center text-center p-6">
                      <span className="material-symbols-outlined text-primary text-5xl mb-2">
                        emoji_events
                      </span>
                      <p className="text-base-content/70 text-sm font-bold">
                        {t('layoutPrizeTable')}
                      </p>
                      <p className="text-base-content/50 text-xs mt-1">
                        {linkedTournament?.name ?? t('noTournamentAssigned')}
                      </p>
                    </div>
                  )}
                  {/* Live indicator */}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 text-xs font-bold text-primary">
                    <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />
                    {t('screenLink')}
                  </div>
                </div>

                {/* Info */}
                <div className="p-5 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-bold text-base">{screen.name}</p>
                      <QrCodeButton
                        token={screen.token}
                        slug={screen.slug}
                        venueCode={venue?.code}
                      />
                    </div>
                    <span
                      className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        screen.isActive
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'bg-base-300 text-base-content/50'
                      }`}
                    >
                      {screen.isActive ? t('active') : t('inactive')}
                    </span>
                  </div>

                  <div
                    className={
                      screen.slug ? 'text-xs text-base-content/60' : 'text-xs text-warning'
                    }
                  >
                    {screen.slug
                      ? t('tvReady', { defaultValue: '可在 TV 清單選取' })
                      : t('tvNotReady', {
                          defaultValue: '尚未可在 TV 清單選取：請設定並儲存螢幕網址。',
                        })}
                    {!screen.slug && editTarget?.id !== screen.id && (
                      <button
                        type="button"
                        onClick={() => handleEditOpen(screen)}
                        className="ml-2 underline font-semibold"
                      >
                        {t('configureScreenUrl', { defaultValue: '設定螢幕網址' })}
                      </button>
                    )}
                  </div>

                  {editTarget?.id === screen.id ? (
                    <div className="space-y-2 pt-2 border-t border-base-300/30">
                      <div>
                        <label className="block text-[10px] font-semibold text-base-content/50 mb-0.5">
                          {t('slug')}
                        </label>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-base-content/50 break-all">
                            /tv/{venue?.code}/
                          </span>
                          <input
                            className="min-w-0 flex-1 rounded-lg border border-base-300 bg-base-300 px-2 py-1.5 text-sm text-base-content font-mono"
                            value={editSlug}
                            onChange={(e) =>
                              setEditSlug(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))
                            }
                            placeholder={t('slugPlaceholder')}
                            maxLength={32}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-semibold text-base-content/50 mb-0.5">
                          {t('displayLayout')}
                        </label>
                        <select
                          className="w-full rounded-lg border border-base-300 bg-base-300 px-3 py-1.5 text-sm text-base-content"
                          value={editLayout}
                          onChange={(e) => setEditLayout(e.target.value)}
                        >
                          {LAYOUT_OPTIONS.map((k) => (
                            <option key={k} value={k}>
                              {t(LAYOUT_KEYS[k] as string)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <label className="block text-xs font-semibold text-base-content/70">
                        {t('screenInsetPercent', { defaultValue: '電視畫面每邊留白 (%)' })}
                        <input
                          type="number"
                          min="0"
                          max="5"
                          step="0.5"
                          value={editInsetPercent}
                          onChange={(event) => setEditInsetPercent(Number(event.target.value))}
                          className="mt-1 w-full rounded-lg border border-base-300 bg-base-300 px-3 py-1.5 text-sm text-base-content"
                        />
                      </label>
                      <p className="text-xs text-base-content/60">
                        {t('screenInsetHint', {
                          defaultValue: '縮小整個畫面，四邊保留黑色邊界；0% 為原始大小。',
                        })}
                      </p>
                      <select
                        className="w-full rounded-lg border border-base-300 bg-base-300 px-3 py-1.5 text-sm text-base-content"
                        value={editTournamentId}
                        onChange={(e) => setEditTournamentId(e.target.value)}
                      >
                        <option value="">{t('noBindTournament')}</option>
                        {tournaments.map((tn) => (
                          <option key={tn.id} value={tn.id}>
                            {tn.name} (
                            {STATUS_KEYS[tn.status]
                              ? t(STATUS_KEYS[tn.status] as string)
                              : tn.status}
                            )
                          </option>
                        ))}
                      </select>
                      {editSlug !== (screen.slug ?? '') && (
                        <p className="text-xs text-warning">
                          {t('screenUrlUnsaved', { defaultValue: '螢幕網址尚未儲存' })}
                        </p>
                      )}
                      {updateMutation.isError && updateMutation.variables?.id === screen.id && (
                        <p role="alert" className="text-xs text-error">
                          {t('screenSaveFailed', {
                            defaultValue: '儲存失敗，請確認網址未重複後重試。',
                          })}
                        </p>
                      )}
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => setEditTarget(null)}
                          className="text-xs px-3 py-1 rounded text-base-content/40 hover:text-base-content/60"
                        >
                          {t('cancel')}
                        </button>
                        <button
                          onClick={() =>
                            updateMutation.mutate({
                              id: screen.id,
                              slug: editSlug || null,
                              layout: editLayout,
                              tournamentId: editTournamentId || null,
                              insetPercent: editInsetPercent,
                            })
                          }
                          disabled={
                            updateMutation.isPending ||
                            !Number.isFinite(editInsetPercent) ||
                            editInsetPercent < 0 ||
                            editInsetPercent > 5 ||
                            !Number.isInteger(editInsetPercent * 2)
                          }
                          className="text-xs px-4 py-1 rounded bg-primary text-black font-bold disabled:opacity-50"
                        >
                          {t('save')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 pt-2 border-t border-base-300/30">
                      <select
                        className="w-full rounded-lg border border-base-300 bg-base-300 px-3 py-1.5 text-sm text-base-content"
                        value={screen.layout}
                        onChange={(e) =>
                          updateMutation.mutate({ id: screen.id, layout: e.target.value })
                        }
                      >
                        {LAYOUT_OPTIONS.map((k) => (
                          <option key={k} value={k}>
                            {t(LAYOUT_KEYS[k] as string)}
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditOpen(screen)}
                          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-base-300 hover:border-primary/40 text-base-content/70 hover:text-primary transition-colors"
                        >
                          <span className="material-symbols-outlined text-sm">edit</span>
                          {t('edit')}
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(t('confirmRegenerate', { name: screen.name }))) {
                              regenTokenMutation.mutate(screen.id);
                            }
                          }}
                          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-base-300 hover:border-amber-500/40 text-base-content/70 hover:text-amber-500 transition-colors"
                        >
                          <span className="material-symbols-outlined text-sm">refresh</span>
                          {t('regenerateToken')}
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(t('confirmDisable', { name: screen.name }))) {
                              removeMutation.mutate(screen.id);
                            }
                          }}
                          className="ml-auto flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-error/30 hover:border-red-500/60 text-red-400 hover:text-red-500 transition-colors"
                        >
                          <span className="material-symbols-outlined text-sm">delete</span>
                          {t('disable')}
                        </button>
                      </div>
                    </div>
                  )}
                  <PairingControls
                    screenId={screen.id}
                    screenName={screen.name}
                    venueName={venue?.name ?? ''}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <CreateModal
          tournaments={tournaments}
          onClose={() => setShowCreate(false)}
          onSave={(data) => createMutation.mutate(data)}
          loading={createMutation.isPending}
        />
      )}

      {showAssign && assignTournamentId && (
        <AssignTournamentModal
          tournament={{
            id: assignTournamentId,
            name:
              tournaments.find((tn) => tn.id === assignTournamentId)?.name ?? assignTournamentId,
          }}
          onClose={() => {
            setShowAssign(false);
            setAssignTournamentId(null);
          }}
          onCreateScreen={() => {
            setShowAssign(false);
            setShowCreate(true);
          }}
        />
      )}
    </div>
  );
}
